// Step cards cursor (<step-cards-cursor>): a chew icon that takes the pointer's place over the
// cards' media ([data-step-cards-cursor-target]). One per section, decoration only (aria-hidden, no pointer events).
// - It follows the pointer closely (an eased follow in requestAnimationFrame, moving only its transform) and fades
//   in and out over the CSS transition (.is-visible).
// - Only with a mouse (hover and a fine pointer) and without reduced motion; the section gets .has-card-cursor
//   while it's on, and only then does the CSS hide the native cursor over the targets.
// - Off while the row is dragged (stay-carousel.js's .is-dragging), where the grabbing cursor shows.
// - The cards move under a still pointer when the page or the row scrolls (the row also glides after a drag), so
//   every scroll re-checks what's under the pointer, a frame later (after the carousel's own scrollend).
if (!customElements.get('step-cards-cursor')) {
  // Share of the remaining distance covered per 60fps frame
  const FOLLOW = 0.5;

  customElements.define(
    'step-cards-cursor',
    class StepCardsCursor extends HTMLElement {
      connectedCallback() {
        this.section = this.parentElement;
        this.query = window.matchMedia(
          '(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)'
        );
        this.onMove = this.onMove.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onLeave = this.onLeave.bind(this);
        this.update = this.update.bind(this);
        this.toggle = () => this.setEnabled(this.query.matches);
        this.query.addEventListener('change', this.toggle);
        this.toggle();
      }

      disconnectedCallback() {
        this.query.removeEventListener('change', this.toggle);
        this.setEnabled(false);
      }

      setEnabled(enabled) {
        if (enabled === !!this.enabled) return;
        this.enabled = enabled;
        this.section.classList.toggle('has-card-cursor', enabled);
        const method = enabled ? 'addEventListener' : 'removeEventListener';
        this.section[method]('pointermove', this.onMove, { passive: true });
        this.section[method]('pointerleave', this.onLeave);
        ['scroll', 'scrollend'].forEach((type) => {
          window[method](type, this.onScroll, { passive: true });
          this.section[method](type, this.onScroll, { capture: true, passive: true });
        });
        if (!enabled) {
          this.onLeave();
          cancelAnimationFrame(this.frame);
          cancelAnimationFrame(this.recheck);
          this.frame = null;
        }
      }

      onMove(event) {
        if (event.pointerType !== 'mouse') return;
        this.inside = true;
        this.pointerX = event.clientX;
        this.pointerY = event.clientY;
        this.check(event.target);
      }

      onLeave() {
        this.inside = false;
        this.hide();
      }

      onScroll() {
        if (!this.inside || this.recheck) return;
        this.recheck = requestAnimationFrame(() => {
          this.recheck = null;
          if (this.inside) this.check(document.elementFromPoint(this.pointerX, this.pointerY));
        });
      }

      check(element) {
        const over =
          element &&
          this.section.contains(element) &&
          element.closest('[data-step-cards-cursor-target]') &&
          !element.closest('.is-dragging');
        over ? this.show() : this.hide();
        if (this.visible) this.follow();
      }

      show() {
        if (this.visible) return;
        this.visible = true;
        // Appear at the pointer, not trailing in from wherever it was last
        this.x = this.pointerX;
        this.y = this.pointerY;
        this.place();
        this.classList.add('is-visible');
      }

      hide() {
        this.visible = false;
        this.classList.remove('is-visible');
      }

      follow() {
        if (this.frame) return;
        this.last = performance.now();
        this.frame = requestAnimationFrame(this.update);
      }

      update(now) {
        const step = 1 - Math.pow(1 - FOLLOW, Math.min(now - this.last, 64) / (1000 / 60));
        this.last = now;
        const dx = this.pointerX - this.x;
        const dy = this.pointerY - this.y;
        if (Math.abs(dx) < 0.1 && Math.abs(dy) < 0.1) {
          this.x = this.pointerX;
          this.y = this.pointerY;
          this.place();
          this.frame = null;
          return;
        }
        this.x += dx * step;
        this.y += dy * step;
        this.place();
        this.frame = requestAnimationFrame(this.update);
      }

      place() {
        this.style.transform = `translate3d(${this.x}px, ${this.y}px, 0)`;
      }
    }
  );
}
